import { useAsyncAction } from "../hooks/useAsyncAction";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type {
  RescueCase,
  RescueEvidenceAnalysisRequest,
  RescueEvidenceAnalysisResponse,
  UpdateRescueCommitmentRequest,
} from "@io-spoke/contracts/rescue";
import { AlertTriangle, RefreshCw, ScanSearch } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@components/ui/button";
import { Skeleton } from "@components/ui/skeleton";
import { PageShell } from "@components/layout/PageShell";
import { RescueAccountPicker } from "@components/rescues/RescueRoomControls";
import { RescueRoomHeader } from "@components/rescues/RescueRoomHeader";
import { RescueCaseFile } from "@components/rescues/RescueCaseFile";
import { EvidenceLabSheet } from "@components/rescues/EvidenceLabSheet";
import {
  analyzeRescueEvidence,
  detectRescue,
  getRescueCases,
  updateRescueCommitment,
} from "../lib/rescues";
import { recordAiOutcome } from "../lib/aiFeedback";

function RescueRoomEmptyState({ onConnect }: { onConnect: () => void }) {
  return (
    <div className="flex min-h-[320px] flex-col items-center justify-center rounded-lg border border-dashed bg-card p-8 text-center">
      <ScanSearch className="h-9 w-9 text-emerald-600" aria-hidden="true" />
      <h2 className="mt-4 text-lg font-semibold">
        No customer risks detected yet
      </h2>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        Connect a source so IO Spoke can evaluate customer conversations and
        work for promises, deadlines, and commercial risk.
      </p>
      <Button type="button" className="mt-5" onClick={onConnect}>
        Connect a data source
      </Button>
    </div>
  );
}

export function RescueRoomPage({
  rescueCaseId,
  navigate,
}: {
  rescueCaseId?: string;
  navigate: (path: string) => void;
}) {
  const loadAction = useAsyncAction();

  const analysisAction = useAsyncAction();
  const approvalAction = useAsyncAction();
  const commitmentAction = useAsyncAction();
  const [cases, setCases] = useState<RescueCase[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(
    rescueCaseId ?? null,
  );
  const [total, setTotal] = useState(0);
  const [currency, setCurrency] = useState("USD");
  const [loading, setLoading] = useState(true);
  const [updatingCommitmentId, setUpdatingCommitmentId] = useState<
    string | null
  >(null);
  const [evidenceLabOpen, setEvidenceLabOpen] = useState(false);
  const [evidenceResult, setEvidenceResult] =
    useState<RescueEvidenceAnalysisResponse | null>(null);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  const selectionVersion = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const [announcement, setAnnouncement] = useState("");
  const [error, setError] = useState<string | null>(null);

  const load = async () =>
    loadAction.run(async () => {
      setLoading(true);
      try {
        const result = await getRescueCases();
        setError(null);
        setCases(result.cases);
        setTotal(result.totalValueAtRiskCents);
        setCurrency(result.currency);
        setSelectedId((current) =>
          current && result.cases.some((item) => item.id === current)
            ? current
            : (result.cases[0]?.id ?? null),
        );
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Unable to load customer findings.",
        );
      } finally {
        setLoading(false);
      }
    });

  useEffect(() => {
    void load();
  }, []);
  useLayoutEffect(() => {
    if (rescueCaseId) {
      const next = cases.some((item) => item.id === rescueCaseId)
        ? rescueCaseId
        : (cases[0]?.id ?? rescueCaseId);
      setSelectedId(next);
    }
  }, [rescueCaseId]);
  useLayoutEffect(() => {
    selectionVersion.current += 1;
    setEvidenceLabOpen(false);
    setEvidenceResult(null);
    setEvidenceError(null);
  }, [selectedId, rescueCaseId]);
  const selected = cases.find((item) => item.id === selectedId) ?? null;
  const totalLabel = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(total / 100);

  const selectCase = (id: string) => {
    selectionVersion.current += 1;
    setSelectedId(id);
    navigate(`/rescues/${id}`);
  };
  const handleEvidenceAnalysis = async (input: RescueEvidenceAnalysisRequest) =>
    analysisAction.run(async () => {
      if (!selected) return;
      const version = selectionVersion.current;
      setEvidenceError(null);
      setEvidenceResult(null);
      try {
        const result = await analyzeRescueEvidence(selected.id, input);
        if (mounted.current && version === selectionVersion.current)
          setEvidenceResult(result);
      } catch (err) {
        if (mounted.current && version === selectionVersion.current)
          setEvidenceError(
            err instanceof Error
              ? err.message
              : "The evidence could not be analyzed.",
          );
      }
    });

  // Quality telemetry only: recording failures must never block the reviewer's action.
  const recordEvidenceOutcome = (
    responseId: string,
    outcome: "accepted" | "rejected",
  ) => {
    void recordAiOutcome(responseId, outcome).catch(() => undefined);
  };

  const handleEvidenceDiscard = () => {
    if (evidenceResult)
      recordEvidenceOutcome(evidenceResult.inference.responseId, "rejected");
    setEvidenceResult(null);
    setEvidenceError(null);
  };

  const handleEvidenceApproval = async () =>
    approvalAction.run(async () => {
      if (!evidenceResult || evidenceResult.outcome !== "proposal") return;
      const reviewed = evidenceResult;
      const version = selectionVersion.current;
      setEvidenceError(null);
      try {
        const result = await detectRescue(reviewed.proposal);
        if (!mounted.current || version !== selectionVersion.current) return;
        recordEvidenceOutcome(reviewed.inference.responseId, "accepted");
        setCases((current) =>
          current.some((item) => item.id === result.rescueCase.id)
            ? current.map((item) =>
                item.id === result.rescueCase.id ? result.rescueCase : item,
              )
            : [result.rescueCase, ...current],
        );
        setTotal((current) =>
          result.created
            ? current + result.rescueCase.annualValueCents
            : current,
        );
        setSelectedId(result.rescueCase.id);
        setEvidenceLabOpen(false);
        setEvidenceResult(null);
        navigate(`/rescues/${result.rescueCase.id}`);
        setAnnouncement(
          `Evidence approved and rescue case updated for ${result.rescueCase.customerName}.`,
        );
        toast.success("Evidence approved and added to the rescue case.");
      } catch (err) {
        if (mounted.current && version === selectionVersion.current)
          setEvidenceError(
            err instanceof Error
              ? err.message
              : "The rescue proposal could not be created.",
          );
      }
    });

  const handleCommitmentUpdate = async (
    commitmentId: string,
    update: UpdateRescueCommitmentRequest,
  ) =>
    commitmentAction.run(async () => {
      if (!selected) return;
      const version = selectionVersion.current;
      setUpdatingCommitmentId(commitmentId);
      try {
        const result = await updateRescueCommitment(
          selected.id,
          commitmentId,
          update,
        );
        if (!mounted.current || version !== selectionVersion.current) return;
        setCases((current) =>
          current.map((item) =>
            item.id === result.rescueCase.id ? result.rescueCase : item,
          ),
        );
        setAnnouncement("Promise Ledger updated.");
        toast.success("Promise Ledger updated.");
      } catch (err) {
        if (mounted.current && version === selectionVersion.current)
          toast.error(
            err instanceof Error
              ? err.message
              : "Unable to update the promise.",
          );
        throw err;
      } finally {
        if (mounted.current) setUpdatingCommitmentId(null);
      }
    });

  return (
    <PageShell>
      <div className="mx-auto flex w-full flex-col gap-6 sm:gap-8">
        <RescueRoomHeader
          caseCount={cases.length}
          showTools={!loading && !error && cases.length > 0}
          totalLabel={totalLabel}
          onAnalyze={() => {
            setEvidenceError(null);
            setEvidenceResult(null);
            setEvidenceLabOpen(true);
          }}
        />

        <p className="sr-only" role="status" aria-live="polite">
          {announcement}
        </p>

        {loading ? <Skeleton className="min-h-[320px] rounded-lg" /> : null}
        {error ? (
          <div
            role="alert"
            className="flex min-h-[320px] flex-col items-center justify-center rounded-lg border border-dashed border-destructive/40 bg-card p-8 text-center"
          >
            <AlertTriangle className="h-8 w-8 text-destructive" />
            <h2 className="mt-4 text-lg font-semibold">
              Customer findings could not be loaded
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">{error}</p>
            <Button
              type="button"
              variant="outline"
              loading={loadAction.pending}
              loadingLabel="Loading findings…"
              onClick={() => void load()}
              className="mt-5 gap-2"
            >
              <RefreshCw className="h-4 w-4" /> Try again
            </Button>
          </div>
        ) : null}
        {!loading && !error && cases.length === 0 ? (
          <RescueRoomEmptyState
            onConnect={() => navigate("/settings/integrations")}
          />
        ) : null}
        {!loading && !error && selected ? (
          <div className="flex flex-col gap-6">
            <RescueCaseFile
              rescueCase={selected}
              onRecoveryCompleted={(entityId) => {
                const caseId = selected.id;
                setCases((current) =>
                  current.map((item) => {
                    if (item.id !== caseId) return item;
                    return {
                      ...item,
                      status:
                        item.status === "detected" ? "active" : item.status,
                      launchedEntityId: item.launchedEntityId ?? entityId,
                    };
                  }),
                );
              }}
              updatingCommitmentId={updatingCommitmentId}
              onUpdateCommitment={handleCommitmentUpdate}
              onOpenWork={(id) => navigate(`/entities/${id}`)}
            />
            <RescueAccountPicker
              cases={cases}
              selectedId={selected.id}
              onSelect={selectCase}
            />
          </div>
        ) : null}
        {selected ? (
          <EvidenceLabSheet
            key={selected.id}
            open={evidenceLabOpen}
            customerName={selected.customerName}
            pending={analysisAction.pending}
            approving={approvalAction.pending}
            error={evidenceError}
            result={evidenceResult}
            onOpenChange={setEvidenceLabOpen}
            onAnalyze={handleEvidenceAnalysis}
            onApprove={handleEvidenceApproval}
            onReset={handleEvidenceDiscard}
          />
        ) : null}
      </div>
    </PageShell>
  );
}
